'use client';

import { isStringValue } from '../../../lib/validation/primitives';

import { ChangeEvent, useMemo, useRef, useState } from 'react';
import Link from 'next/link';

import { Badge } from '@/components/badge';
import { SRT_TO_VTT_FAQS } from './faqs';
import { convertSubtitles, parseSubtitles, type SubtitleFormat } from '@engine/tools-shell';
import { cn } from '@/lib/utils';
import { useLocalToolRun } from '@/components/free-tools/local-tool-runs';

const TARGETS: Array<{ key: SubtitleFormat; label: string; hint: string; ext: string }> = [
  { key: 'vtt', label: 'VTT', hint: 'Web / HTML5', ext: 'vtt' },
  { key: 'srt', label: 'SRT', hint: 'YouTube, Premiere', ext: 'srt' },
  { key: 'txt', label: 'TXT', hint: 'Plain text', ext: 'txt' },
];

export function SrtToVttConverterClient() {
  const [input, setInput] = useState('');
  const [target, setTarget] = useState<SubtitleFormat>('vtt');
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { output, cueCount, error } = useMemo(() => {
    const trimmed = input.trim();

    // SAFETY: The surrounding typed contract establishes the asserted representation.
    if (!trimmed)
      return {
        output: '',
        cueCount: 0,
        error:
          /* SAFETY: The surrounding typed contract establishes the asserted representation. */ null as
            | string
            | null,
      };

    try {
      const cues = parseSubtitles(input);

      if (cues.length === 0) {
        return {
          output: '',
          cueCount: 0,
          error: 'No subtitle cues found. Paste valid SRT or VTT content with timecodes.',
        };
      }

      return { output: convertSubtitles(cues, target), cueCount: cues.length, error: null };
    } catch {
      return {
        output: '',
        cueCount: 0,
        error: 'Could not parse this file. Check it is valid SRT or VTT.',
      };
    }
  }, [input, target]);

  const [fileName, setFileName] = useState<string | null>(null);

  useLocalToolRun(
    output
      ? {
          title: fileName ?? 'Pasted subtitles',
          summary: `${cueCount.toLocaleString()} cues · to ${target.toUpperCase()}`,
          state: { input, target, fileName },
        }
      : null,
    (state) => {
      setInput(typeof state.input === 'string' ? state.input : '');
      if (state.target === 'vtt' || state.target === 'srt' || state.target === 'txt') setTarget(state.target);
      setFileName(typeof state.fileName === 'string' ? state.fileName : null);
    }
  );

  function handleFile(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];

    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setInput(isStringValue(reader.result) ? reader.result : '');
    reader.readAsText(file);
  }

  async function handleCopy(): Promise<void> {
    if (!output) return;

    try {
      await navigator.clipboard.writeText(output);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('idle');
    }
  }

  const downloadHref = output ? `data:text/plain;charset=utf-8,${encodeURIComponent(output)}` : '';
  const downloadName = `subtitles.${TARGETS.find((t) => t.key === target)?.ext ?? 'txt'}`;

  return (
    <div className="space-y-6">
      {/* H1 and intro live in FreeToolPageShell; only live status stays here. */}

      <div className="rounded-[28px] border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5 sm:p-6 grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium" htmlFor="subs-input">
              Paste SRT or VTT
            </label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-white/15"
            >
              Upload file
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".srt,.vtt,.txt"
              onChange={handleFile}
              className="sr-only"
            />
          </div>
          <textarea
            id="subs-input"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (!e.target.value.trim()) setFileName(null);
            }}
            rows={16}
            placeholder={'1\n00:00:00,000 --> 00:00:02,500\nHello and welcome.'}
            className="min-h-52 w-full resize-y rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] p-4 font-mono text-xs leading-5"
          />
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {TARGETS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTarget(t.key)}
                className={cn(
                  'inline-flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors',
                  target === t.key
                    ? 'border-violet-400/70 bg-violet-500/10'
                    : 'border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] hover:border-amber-400/45'
                )}
              >
                <span className="text-sm font-semibold">Convert to {t.label}</span>
                <span className="text-[11px] text-[rgb(var(--foreground-secondary))]">
                  {t.hint}
                </span>
              </button>
            ))}
          </div>
          <textarea
            readOnly
            value={error ? '' : output}
            rows={14}
            placeholder="Converted subtitles appear here"
            className="min-h-52 w-full resize-y rounded-lg border border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] p-4 font-mono text-xs leading-5"
          />
          {error ? <p className="text-sm text-[rgb(var(--error))]">{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!output}
              className="inline-flex min-h-10 items-center justify-center rounded-full border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/15 disabled:opacity-50"
            >
              {copyState === 'copied' ? 'Copied' : 'Copy'}
            </button>
            <a
              href={downloadHref}
              download={downloadName}
              aria-disabled={!output}
              className={cn(
                'inline-flex min-h-10 items-center justify-center rounded-full border border-white/20 bg-white/10 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/15',
                !output && 'pointer-events-none opacity-50'
              )}
            >
              Download .{TARGETS.find((t) => t.key === target)?.ext}
            </a>
          </div>
        </div>
      </div>

      <details className="text-sm text-[rgb(var(--foreground-secondary))]">
        <summary>About this tool</summary>{' '}
        <div className="space-y-4">
          <p className="text-[rgb(var(--foreground-secondary))]">
            Convert subtitles between SRT, VTT, and TXT instantly in your browser. Paste your file
            or upload it. Nothing is uploaded to a server, and it&apos;s completely free.
          </p>
          <p className="text-sm text-[rgb(var(--foreground-secondary))]">
            Need subtitles from audio or video instead?{' '}
            <Link href="/tools/subtitle-generator" className="underline underline-offset-2">
              Generate SRT/VTT from a recording
            </Link>
            .
          </p>
        </div>
      </details>
    </div>
  );
}

/**
 * Long-form SEO cards for this tool. Rendered by the page through FreeToolPageShell's `body`
 * slot, below the hero, so the tool, its "About"/"How this works" panel and the platform
 * chips stay together in the first view.
 */
export function SrtToVttSeoSections() {
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">SRT vs VTT</h2>
          <p className="mt-3 text-sm text-[rgb(var(--foreground-secondary))]">
            <strong>SRT</strong> (SubRip) uses comma milliseconds and numbered cues, used by
            YouTube, Premiere, and CapCut. <strong>VTT</strong> (WebVTT) uses dot milliseconds and a{' '}
            <code>WEBVTT</code> header, the standard for HTML5 <code>&lt;track&gt;</code> captions.
            This tool converts between them losslessly.
          </p>
        </section>
        <section className="rounded-xl border border-violet-400/20 bg-[rgb(var(--surface))] p-5">
          <h2 className="text-base font-semibold">Private by design</h2>
          <p className="mt-3 text-sm text-[rgb(var(--foreground-secondary))]">
            Conversion happens entirely in your browser, so your subtitle file never leaves your
            device. No signup, no upload, no limits.
          </p>
        </section>
      </div>

      {/* Soft upsell: the only conversion surface on this client-side tool. */}
      <div className="mt-12">
        <div className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-r from-fuchsia-500/10 via-violet-500/10 to-amber-500/10 p-6 sm:flex sm:items-center sm:justify-between sm:gap-6">
          <div className="space-y-1">
            <p className="text-base font-semibold text-[rgb(var(--foreground))]">
              Need subtitles from the source audio or video?
            </p>
            <p className="text-sm text-[rgb(var(--foreground-secondary))]">
              Generate timestamped SRT, VTT, and TXT captions directly from any recording, free for
              files up to 10 minutes.
            </p>
          </div>
          <Link
            href="/tools/subtitle-generator"
            className="mt-3 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-95 sm:mt-0"
          >
            Generate subtitles →
          </Link>
        </div>
      </div>
    </>
  );
}
